import type { IAugmentedJQuery, IComponentController, IScope } from "angular";
import $ from "jquery";
import "@progress/kendo-ui/js/kendo.data";
import "@progress/kendo-ui/js/kendo.grid";

import "angular-agility";
import "angular-translate";

import type { ILanguage, IResponse } from "App/Modules/Models";
import { TranslationsFunctions } from "App/Modules/Models";
import type { ITranslationsRepository } from "App/Modules/Repositories";

export default class TranslationsComponentController implements IComponentController {
    public static $inject = [
        "$scope",
        "$log",
        "$translate",
        "aaNotify",
        "mmTranslationsRepository",
        "irLanguageCacheService",
    ];

    public languages: ILanguage[];

    public selectedLanguage: string;

    public translationDataSource: kendo.data.DataSource;

    public translationsFilterText: string;

    public untranslatedKeysDataSource: kendo.data.DataSource;

    public lastError: IResponse<any>;

    public translationsGridColumns: kendo.ui.GridColumn[];

    constructor(
        private readonly $scope: IScope,
        private readonly $log: ng.ILogService,
        private readonly $translate: angular.translate.ITranslateService,
        private readonly aaNotify: any,
        private readonly translationsRepository: ITranslationsRepository,
        private readonly languageCacheService: { clearCache: () => void },
    ) {}

    public $onInit() {
        this.checkLanguages();

        this.translationsGridColumns = [
            { field: "translationKeyId", title: "TranslationKey", width: 140 },
            {
                field: "fallbackTranslation",
                title: "Fallback translation",
                template:
                    "<pre ng-non-bindable style='font: inherit; white-space: pre-wrap;'>#:fallbackTranslation#</pre>",
                width: 250,
            },
            {
                field: "value",
                title: "Translation",
                template: "<pre ng-non-bindable style='font: inherit; white-space: pre-wrap;'>#:value#</pre>",
                editor: (container: JQuery, options: kendo.ui.GridColumnEditorOptions) => {
                    $(
                        `<textarea style="width: 95%; margin: -5px -5px -10px;" data-bind="value: ${options.field}"></textarea>`,
                    ).appendTo(container);
                },
            },
            {
                field: "format",
                title: "Format",
                width: 70,
                values: [
                    { value: "plain", text: "Plain" },
                    { value: "messageformat", text: "MessageFormat" },
                    { value: "format", text: "Format" },
                ],
            },
            { field: "variables", title: "Variables", width: 140 },
            { field: "description", title: "Description", width: 140 },
        ];

        this.translationDataSource = new kendo.data.DataSource({
            autoSync: true,
            batch: false,
            pageSize: 25,
            schema: {
                model: {
                    fields: {
                        description: { editable: false },
                        fallbackTranslation: { editable: false },
                        format: { editable: false },
                        languageId: { editable: false },
                        translationKeyId: { editable: false },
                        value: {
                            editable: true,
                            validation: {
                                compileMessageFormatValidation: (input: IAugmentedJQuery) => {
                                    return TranslationsFunctions.compileMessage(input, "value");
                                },
                                messageFormatValidation: (input: IAugmentedJQuery) => {
                                    return TranslationsFunctions.checkMessageFormat(input, "value");
                                },
                                required: false,
                            },
                        },
                        variables: { editable: false },
                    },
                    id: "translationKeyId",
                },
            },
            transport: {
                read: ((e: kendo.data.DataSourceTransportOptions) => {
                    if (!this.selectedLanguage) {
                        e.error("Loading...");
                        return;
                    }

                    this.translationsRepository.getTranslations(this.selectedLanguage).subscribe(
                        (translations) => {
                            if (translations.data) {
                                e.success(translations.data);
                            }
                        },
                        (error) => {
                            this.lastError = error;
                        },
                    );
                }) as any,
                update: (e: kendo.data.DataSourceTransportOptions) => {
                    this.translationsRepository
                        .putTranslation(e.data.languageId, e.data.translationKeyId, e.data)
                        .subscribe(
                            (translation) => {
                                if (translation.data) {
                                    e.success(translation.data);

                                    this.$translate("Entities.Translations.UpdatedTranslation", {
                                        key: e.data.translationKeyId,
                                    }).then((message) => this.aaNotify.success(message));
                                }
                                this.languageCacheService.clearCache();
                            },
                            (error) => {
                                this.lastError = error;
                            },
                        );
                },
            } as any,
        });

        this.untranslatedKeysDataSource = new kendo.data.DataSource({
            autoSync: true,
            batch: false,
            pageSize: 25,
            schema: {
                model: {
                    fields: {
                        description: { editable: false },
                        fallbackTranslation: {
                            editable: false,
                            template: "<span ng-non-bindable>#=fallbackTranslation#</span>",
                        },
                        format: { editable: false },
                        languageId: { editable: false },
                        translationKeyId: { editable: false },
                        value: {
                            editable: true,
                            template: "<span ng-non-bindable>#=value#</span>",
                            validation: { required: false },
                        },
                        variables: { editable: false },
                    },
                    id: "translationKeyId",
                },
            },
            transport: {
                read: (e: kendo.data.DataSourceTransportOptions) => {
                    if (!this.selectedLanguage) {
                        e.error("Loading...");
                        return;
                    }
                    this.translationsRepository.getUntranslatedKeys(this.selectedLanguage).subscribe(
                        (keys) => {
                            if (keys.data) {
                                e.success(keys.data);
                            }
                        },
                        (error) => {
                            this.lastError = error;
                        },
                    );
                },
                update: (e: kendo.data.DataSourceTransportOptions) => {
                    this.translationsRepository
                        .putTranslation(e.data.languageId, e.data.translationKeyId, e.data)
                        .subscribe(
                            (translations) => {
                                if (translations.data) {
                                    this.untranslatedKeysDataSource.remove(e.data);
                                    this.translationDataSource.add(e.data);
                                    e.success(e.data);
                                    this.$translate("Entities.Translations.UpdatedTranslation", {
                                        key: e.data.translationKeyId,
                                    }).then((message) => this.aaNotify.success(message));
                                }
                            },
                            (error) => {
                                this.lastError = error;
                            },
                        );
                },
            } as any,
        });

        this.$scope.$watch(
            "$ctrl.selectedLanguage",
            () => {
                this.languageChanged();
            },
            true,
        );
    }

    public checkLanguages() {
        this.translationsRepository.getLanguages().subscribe(
            (response) => {
                if (response.data) {
                    this.languages = response.data;
                    if (this.languages.length > 0) {
                        this.selectedLanguage = this.languages[0].id;
                        this.languageChanged();
                    }
                }
            },
            (error) => {
                this.lastError = error;
            },
        );
    }

    public languageChanged() {
        this.translationDataSource.read();
        this.untranslatedKeysDataSource.read();
    }

    public resetTranslationsFilter() {
        this.translationsFilterText = null;

        this.translationDataSource.filter({
            filters: [],
            logic: "or",
        });
        this.untranslatedKeysDataSource.filter({
            filters: [],
            logic: "or",
        });
    }

    public translationsFilterUpdated() {
        this.translationDataSource.filter({
            filters: [
                {
                    field: "translationKeyId",
                    operator: "contains",
                    value: this.translationsFilterText,
                },
                {
                    field: "fallbackTranslation",
                    operator: "contains",
                    value: this.translationsFilterText,
                },
                {
                    field: "value",
                    operator: "contains",
                    value: this.translationsFilterText,
                },
            ],
            logic: "or",
        });

        this.untranslatedKeysDataSource.filter({
            filters: [
                {
                    field: "translationKeyId",
                    operator: "contains",
                    value: this.translationsFilterText,
                },
                {
                    field: "fallbackTranslation",
                    operator: "contains",
                    value: this.translationsFilterText,
                },
                {
                    field: "value",
                    operator: "contains",
                    value: this.translationsFilterText,
                },
            ],
            logic: "or",
        });
    }
}
